<!--
  Expenses — what it costs to keep the company running, a month at a time.

  Three sections behind one month: what was spent, the standing monthly
  payments, and the buckets they fall in. The month lives in the URL, so a
  month of overheads is a link that can be sent.

  The figure typed is the TOTAL AS PRINTED on the statement, tax included, and
  the ex-GST figure the P&L will actually use is shown under it as the user
  types — so what the app does with the number is never a surprise. Every
  split is computed again on the server; the preview here is only a preview.

  Look: the admin hero and the Rates screen's card-per-thing, with the one
  inline form and inline Yes/No banners. No modals anywhere in this app.
-->
<script lang="ts">
  import { api, isApiError } from '../../lib/api';
  import { backOutGst, firstOfMonth } from '../../lib/calc/expenses';
  import { priceInForce } from '../../lib/calc/pnl';
  import { money } from '../../lib/money';
  import { linkHandler, location, navigate, routePath } from '../../lib/router';
  import { notify, notifyError, todayIso } from '../../lib/store';
  import type {
    EquipmentLease,
    Expense,
    ExpenseCategory,
    ExpenseCategoryKind,
    SaveExpenseCategoryRequest,
    SaveExpenseRequest,
    SaveLeaseRequest,
    TaxRate
  } from '../../lib/types';
  import AdminHero from './AdminHero.svelte';
  import StatementImport from './StatementImport.svelte';
  import './admin-look.css';

  type Tab = 'spending' | 'leases' | 'categories' | 'import';
  let tab = $state<Tab>('spending');

  const MONTH = /^\d{4}-\d{2}$/;

  const month = $derived.by(() => {
    const asked = $location.query.get('month') ?? '';
    return MONTH.test(asked) ? asked : todayIso().slice(0, 7);
  });

  let expenses = $state<Expense[]>([]);
  let leases = $state<EquipmentLease[]>([]);
  let categories = $state<ExpenseCategory[]>([]);
  let taxRates = $state<TaxRate[]>([]);
  let overheads = $state(0);
  let gstPaid = $state(0);
  /** True only before the very first load finishes — gates the full-page
   *  spinner, which replaces every tab's body including whatever local state
   *  it holds (an open inline form, a screen's own in-progress review). A
   *  refresh after that first load must never flip this back to true: an
   *  import committing, or the month changing while a tab is mid-edit, would
   *  otherwise unmount and remount that tab's body on every reload — which is
   *  exactly what silently wiped the statement-import screen's reconciliation
   *  banner before this was tracked apart from `refreshing` below. */
  let loading = $state(true);
  /** True while a load is in flight AFTER the first one. Shown as a small
   *  spinner in the scopebar — never as `loading`, which would swap
   *  `{:else}`'s branch out from under whatever tab is mounted. */
  let refreshing = $state(false);
  let loadedOnce = false;

  async function load(forMonth: string) {
    if (loadedOnce) refreshing = true;
    else loading = true;
    try {
      const [m, c, t] = await Promise.all([
        api.listExpenses(forMonth),
        api.listExpenseCategories(),
        api.listTaxRates()
      ]);
      expenses = m.expenses;
      leases = m.leases;
      overheads = m.overheads;
      gstPaid = m.expense_gst_paid;
      categories = c.expense_categories;
      taxRates = t.tax_rates;
    } catch (cause) {
      notifyError(cause, 'Could not load the expenses');
    } finally {
      loading = false;
      refreshing = false;
      loadedOnce = true;
    }
  }

  $effect(() => {
    void load(month);
  });

  function setMonth(value: string) {
    if (MONTH.test(value)) navigate(routePath.adminExpenses(value), { replace: true, keepScroll: true });
  }

  /** The GST percentage in force on a date — never the constant 5. */
  function gstOn(onDate: string): number {
    return priceInForce(taxRates.filter((r) => r.kind === 'gst'), onDate)?.percent ?? 0;
  }

  const liveCategories = $derived(categories.filter((c) => c.archived === 0));
  const archivedCategories = $derived(categories.filter((c) => c.archived === 1));

  function categoryName(id: number | null): string {
    if (id === null) return 'Uncategorised';
    return categories.find((c) => c.id === id)?.name ?? 'Uncategorised';
  }

  /**
   * Why a row does not move the Overheads total, in the owner's words — or
   * null when it does. No expense ever does: monthlyOverheads() (see
   * lib/calc/expenses.ts) sums only the standing monthly costs below, so
   * nothing here is a candidate for that figure regardless of what it is
   * filed under.
   *
   * `counts_toward_total` used to have a branch here too, but an expense's
   * copy of that flag is as dead as `is_overhead` was — admin-breakdown.ts
   * says outright that Total Breakdown never reads it either, so an
   * unticked expense is not excluded from anything. The column stays (same
   * reasoning as `is_overhead`: no rebuild for a flag nobody reads), but
   * nothing here claims it does something. Only `kind` still excludes a row
   * — money moved rather than spent, which Total Breakdown does honour.
   */
  function exclusionReason(expense: Expense): string | null {
    const category = expense.category_id === null ? undefined : categories.find((c) => c.id === expense.category_id);
    if (category?.kind === 'transfer') return 'Money moved, not spent';
    return null;
  }

  /** Both ends inclusive: a lease starting or ending this month is paid this month. */
  function runningIn(lease: EquipmentLease, inMonth: string): boolean {
    if (lease.start_month > inMonth) return false;
    return lease.end_month === null || inMonth <= lease.end_month;
  }

  /* --- the one inline form, keyed by what it is for ---------------------- */

  interface FormState {
    date: string;
    merchant: string;
    description: string;
    category: string;
    amount: string;
    hadGst: boolean;
    counts: boolean;
    note: string;
    name: string;
    start: string;
    end: string;
    /** What kind of standing cost this is, in the owner's own words — 'Lease' by default. */
    kindLabel: string;
    /** 'expense' if this category is spending; 'transfer' if it is only money moved. */
    kind: ExpenseCategoryKind;
  }

  function blankForm(): FormState {
    return {
      date: `${month}-01`,
      merchant: '',
      description: '',
      category: '',
      amount: '',
      hadGst: false,
      counts: true,
      note: '',
      name: '',
      start: month,
      end: '',
      kindLabel: '',
      kind: 'expense'
    };
  }

  /** 'expense:new', 'expense:12', 'lease:new', 'lease:3', 'cat:new', 'cat:5'. */
  let openForm = $state<string | null>(null);
  let confirmDelete = $state<string | null>(null);
  let formError = $state<string | null>(null);
  let saving = $state(false);
  let form = $state<FormState>(blankForm());
  /** Original category values, seeded when the form opens, for change detection. */
  let originalCategoryValues = $state<{ name: string; kind: ExpenseCategoryKind } | null>(null);

  function closeForm() {
    openForm = null;
    confirmDelete = null;
    formError = null;
  }

  function openExpense(expense: Expense | null) {
    form = blankForm();
    if (expense) {
      form.date = expense.incurred_on;
      form.merchant = expense.merchant;
      form.description = expense.description;
      form.category = expense.category_id === null ? '' : String(expense.category_id);
      form.amount = String(expense.amount_total);
      form.hadGst = expense.had_gst === 1;
      // form.counts is NOT seeded from expense.counts_toward_total: that
      // column is stored and left alone (see exclusionReason above), but
      // there is no control on this form for it any more.
      form.note = expense.note;
    }
    formError = null;
    confirmDelete = null;
    openForm = expense ? `expense:${expense.id}` : 'expense:new';
  }

  function openLease(lease: EquipmentLease | null) {
    form = blankForm();
    if (lease) {
      form.name = lease.name;
      form.amount = String(lease.monthly_amount);
      form.hadGst = lease.had_gst === 1;
      form.start = lease.start_month;
      form.end = lease.end_month ?? '';
      form.counts = lease.counts_toward_total === 1;
      form.kindLabel = lease.kind_label;
      form.note = lease.note;
    }
    formError = null;
    confirmDelete = null;
    openForm = lease ? `lease:${lease.id}` : 'lease:new';
  }

  function openCategory(category: ExpenseCategory | null) {
    form = blankForm();
    if (category) {
      form.name = category.name;
      form.kind = category.kind;
      originalCategoryValues = { name: category.name, kind: category.kind };
    } else {
      originalCategoryValues = null;
    }
    formError = null;
    confirmDelete = null;
    openForm = category ? `cat:${category.id}` : 'cat:new';
  }

  function typedAmount(): number {
    const n = Number(form.amount.trim());
    return Number.isFinite(n) ? n : 0;
  }

  // A lease has no date of its own, so it is previewed at the rate in force on
  // the first of the month being reported — the same date the server uses.
  const previewDate = $derived(
    openForm?.startsWith('lease')
      ? firstOfMonth(MONTH.test(form.start) ? form.start : month)
      : form.date || firstOfMonth(month)
  );
  const preview = $derived(backOutGst(typedAmount(), form.hadGst, gstOn(previewDate)));

  function readAmount(): number | null {
    const trimmed = form.amount.trim();
    const n = trimmed === '' ? NaN : Number(trimmed);
    return Number.isFinite(n) && n >= 0 ? n : null;
  }

  function failed(cause: unknown): string {
    return isApiError(cause)
      ? Object.values(cause.fields ?? {}).join('; ') || cause.message
      : 'Could not save. Check the connection and try again.';
  }

  async function saveExpense(expenseId: number | null) {
    if (saving) return;
    const amount = readAmount();
    if (amount === null) {
      formError = 'Enter the total as printed on the statement.';
      return;
    }
    saving = true;
    formError = null;
    const body: SaveExpenseRequest = {
      incurred_on: form.date,
      category_id: form.category === '' ? null : Number(form.category),
      merchant: form.merchant,
      description: form.description,
      amount_total: amount,
      had_gst: form.hadGst,
      // counts_toward_total deliberately left out of this body: the column
      // survives on the row (see exclusionReason above) but nothing reads it
      // for an expense, so there is nothing here to set it FROM. Omitting it
      // leaves whatever the row already has untouched, the same way leaving
      // `is_overhead` out of a category save does.
      note: form.note
    };
    try {
      if (expenseId === null) await api.createExpense(body);
      else await api.updateExpense(expenseId, body);
      notify({ tone: 'ok', title: 'Expense saved' });
      closeForm();
      await load(month);
    } catch (cause) {
      formError = failed(cause);
    } finally {
      saving = false;
    }
  }

  async function saveLease(leaseId: number | null) {
    if (saving) return;
    const amount = readAmount();
    if (form.name.trim() === '') {
      formError = 'Give it a name.';
      return;
    }
    if (amount === null) {
      formError = 'Enter the monthly payment.';
      return;
    }
    saving = true;
    formError = null;
    const body: SaveLeaseRequest = {
      name: form.name,
      monthly_amount: amount,
      had_gst: form.hadGst,
      start_month: form.start,
      end_month: form.end === '' ? null : form.end,
      counts_toward_total: form.counts,
      // Blank means "didn't say" — omitted rather than sent empty, so the
      // server's own default ('Lease' on a new row, the row's own value on
      // an edit) applies instead of tripping the "is required" check.
      kind_label: form.kindLabel.trim() === '' ? undefined : form.kindLabel.trim(),
      note: form.note
    };
    try {
      if (leaseId === null) await api.createLease(body);
      else await api.updateLease(leaseId, body);
      notify({ tone: 'ok', title: 'Standing cost saved' });
      closeForm();
      await load(month);
    } catch (cause) {
      formError = failed(cause);
    } finally {
      saving = false;
    }
  }

  async function saveCategory(categoryId: number | null) {
    if (saving) return;
    if (form.name.trim() === '') {
      formError = 'Give the category a name.';
      return;
    }
    saving = true;
    formError = null;
    const body: SaveExpenseCategoryRequest = { name: form.name };
    // For updates, only include the flag if it actually changed, to avoid
    // overwriting a concurrent modification made elsewhere (another tab,
    // another admin, etc). `is_overhead` is deliberately never sent from
    // here any more — nothing in the P&L reads it (see monthlyOverheads in
    // lib/calc/expenses.ts), so there is no control for it left on screen,
    // and the server keeps a row's existing value, or its own default for a
    // new one, whenever the field is left out of the request.
    if (originalCategoryValues) {
      if (form.kind !== originalCategoryValues.kind) {
        body.kind = form.kind;
      }
    } else {
      // New category: send the kind as it has no prior value to compare.
      body.kind = form.kind;
    }
    try {
      if (categoryId === null) await api.createExpenseCategory(body);
      else await api.updateExpenseCategory(categoryId, body);
      notify({ tone: 'ok', title: 'Category saved' });
      closeForm();
      await load(month);
    } catch (cause) {
      formError = failed(cause);
    } finally {
      saving = false;
    }
  }

  /**
   * A lease's toggle is saved on the spot rather than behind the form: it is
   * the control the owner reaches for most, to leave a standing cost out of
   * a month's dashboard total without hunting through the edit form for it.
   */
  async function setLeaseCounts(lease: EquipmentLease, counts: boolean) {
    try {
      await api.updateLease(lease.id, { counts_toward_total: counts });
      await load(month);
    } catch (cause) {
      notifyError(cause, 'Could not change that lease');
    }
  }

  async function setCategoryArchived(category: ExpenseCategory, archived: boolean) {
    try {
      await api.updateExpenseCategory(category.id, { archived });
      confirmDelete = null;
      notify({ tone: 'ok', title: archived ? 'Category archived' : 'Category restored' });
      await load(month);
    } catch (cause) {
      notifyError(cause, 'Could not change that category');
    }
  }

  async function removeExpense(expenseId: number) {
    try {
      await api.deleteExpense(expenseId);
      confirmDelete = null;
      notify({ tone: 'ok', title: 'Expense removed' });
      await load(month);
    } catch (cause) {
      notifyError(cause, 'Could not remove that expense');
    }
  }

  async function removeLease(leaseId: number) {
    try {
      await api.deleteLease(leaseId);
      confirmDelete = null;
      notify({ tone: 'ok', title: 'Standing cost removed' });
      await load(month);
    } catch (cause) {
      notifyError(cause, 'Could not remove that lease');
    }
  }

  const stats = $derived([
    { value: money(overheads), label: 'standing monthly costs' },
    { value: String(expenses.length), label: `expenses in ${month}` },
    { value: money(gstPaid), label: 'gst paid' }
  ]);

  const TABS: { id: Tab; label: string }[] = [
    { id: 'spending', label: 'Spending' },
    { id: 'leases', label: 'Standing monthly costs' },
    { id: 'categories', label: 'Categories' },
    { id: 'import', label: 'Import statement' }
  ];

  function leaseMonths(lease: EquipmentLease): string {
    return lease.end_month === null ? `from ${lease.start_month}` : `${lease.start_month} – ${lease.end_month}`;
  }
</script>

<AdminHero kicker="Admin · Performance" title="Expenses" {stats}>
  {#snippet links()}
    <a class="a-chip" href={routePath.admin({ month })} onclick={linkHandler}>
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 12H5m7-7-7 7 7 7" /></svg>
      Dashboard
    </a>
    <a class="a-chip" href={routePath.adminRates()} onclick={linkHandler}>Rates</a>
  {/snippet}
</AdminHero>

<div class="scopebar">
  <label class="monthpick">
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </svg>
    <input
      type="month"
      value={month}
      aria-label="Month"
      onchange={(e) => setMonth((e.currentTarget as HTMLInputElement).value)}
    />
  </label>
  <span class="scopebar__label a-num">Standing monthly costs · {money(overheads)}</span>
  {#if refreshing}<span class="spinner" aria-hidden="true"></span>{/if}
</div>

<div class="tabs" role="tablist">
  {#each TABS as t (t.id)}
    <button class="tab" type="button" aria-pressed={tab === t.id} onclick={() => { tab = t.id; closeForm(); }}>
      {t.label}
    </button>
  {/each}
</div>

<!--
  The money field and its live ex-GST readout. Shared by the expense and the
  lease form because both are typed the same way: the total as printed, plus a
  tick for whether that total had GST inside it.
-->
{#snippet amountField(label: string)}
  <label class="field">
    <span class="label">{label}</span>
    <input type="text" inputmode="decimal" bind:value={form.amount} placeholder="0.00" />
  </label>
  <label class="check">
    <input type="checkbox" bind:checked={form.hadGst} />
    This total had GST in it
  </label>
  <p class="split">
    <span>Ex-GST</span>
    <b class="a-num">{money(preview.amount_ex_gst)}</b>
    <small class="a-num">GST {money(preview.gst_amount)}</small>
  </p>
{/snippet}

{#snippet formButtons(save: () => void)}
  {#if formError}<p class="field__flag">{formError}</p>{/if}
  <div class="rateform__btns">
    <button class="a-cta" type="button" disabled={saving} onclick={save}>Save</button>
    <button class="btn" type="button" onclick={closeForm}>Cancel</button>
  </div>
{/snippet}

{#snippet expenseForm(expenseId: number | null)}
  <div class="rateform">
    <label class="field">
      <span class="label">Date</span>
      <input type="date" bind:value={form.date} />
    </label>
    <label class="field">
      <span class="label">Merchant</span>
      <input type="text" bind:value={form.merchant} placeholder="e.g. Petro-Canada" />
    </label>
    <label class="field">
      <span class="label">What it was for</span>
      <input type="text" bind:value={form.description} />
    </label>
    <label class="field">
      <span class="label">Category</span>
      <select bind:value={form.category}>
        <option value="">Uncategorised</option>
        {#each liveCategories as category (category.id)}
          <option value={String(category.id)}>{category.name}</option>
        {/each}
      </select>
    </label>
    {@render amountField('Total as printed on the statement')}
    <!-- No "counts toward the monthly total" control here: an expense's
         copy of that flag governs nothing (see exclusionReason above and
         saveExpense's body) — the equivalent control on a standing monthly
         cost below still does. -->
    <label class="field">
      <span class="label">Note</span>
      <input type="text" bind:value={form.note} />
    </label>
    {@render formButtons(() => void saveExpense(expenseId))}
  </div>
{/snippet}

{#snippet categoryForm(categoryId: number | null)}
  <div class="rateform">
    <label class="field">
      <span class="label">Name</span>
      <input type="text" bind:value={form.name} placeholder="e.g. Permits" />
    </label>
    <label class="field">
      <span class="label">Money spent or money moved</span>
      <select bind:value={form.kind}>
        <option value="expense">Money spent</option>
        <option value="transfer">Money moved</option>
      </select>
      <span class="field__hint">
        Money moved is a card payment or a transfer between your own accounts. It is recorded, but
        never counted as spending.
      </span>
    </label>
    {@render formButtons(() => void saveCategory(categoryId))}
  </div>
{/snippet}

{#snippet leaseForm(leaseId: number | null)}
  <div class="rateform">
    <label class="field">
      <span class="label">What it's for</span>
      <input type="text" bind:value={form.name} placeholder="e.g. Excavator, Insurance" />
    </label>
    <label class="field">
      <span class="label">What kind</span>
      <input type="text" bind:value={form.kindLabel} placeholder="Lease" />
    </label>
    {@render amountField('Monthly payment as invoiced')}
    <label class="field">
      <span class="label">First month</span>
      <input type="month" bind:value={form.start} />
    </label>
    <label class="field">
      <span class="label">Last month</span>
      <input type="month" bind:value={form.end} />
      <span class="field__hint">Leave empty while it's still running.</span>
    </label>
    <label class="check">
      <input type="checkbox" bind:checked={form.counts} />
      Counts toward the monthly total
    </label>
    <label class="field">
      <span class="label">Note</span>
      <input type="text" bind:value={form.note} />
    </label>
    {@render formButtons(() => void saveLease(leaseId))}
  </div>
{/snippet}

{#if loading}
  <p class="meta center"><span class="spinner" aria-hidden="true"></span> Loading…</p>
{:else if tab === 'spending'}
  <p class="note">
    Type the total as it is printed on the statement, tax included, and tick whether it had GST. The
    ex-GST figure under it is what the P&amp;L uses.
  </p>

  {#if expenses.length === 0}
    <div class="empty">
      <strong>Nothing spent in {month}</strong>
      <span class="meta">Standing costs still count toward the month's overheads.</span>
    </div>
  {:else}
    <ul class="a-list">
      {#each expenses as expense, i (expense.id)}
        {@const key = `expense:${expense.id}`}
        {@const excluded = exclusionReason(expense)}
        <li style="--i: {i}">
          <div class="a-card rate">
            <span
              class="a-card__rail"
              class:a-card__rail--muted={excluded !== null}
              aria-hidden="true"
            ></span>
            <div class="rate__body">
              <div class="rate__head">
                <div class="rate__name">
                  {expense.merchant || expense.description || 'Expense'}
                  <span class="rate__sub">
                    <span class="a-num">{expense.incurred_on}</span> · {categoryName(expense.category_id)}
                    {#if expense.merchant && expense.description}· {expense.description}{/if}
                    {#if expense.source === 'import'}<span class="imported">imported</span>{/if}
                  </span>
                </div>
                <div class="rate__now">
                  <b class="a-num">{money(expense.amount_total)}</b>
                  <small class="a-num">ex-GST {money(expense.amount_ex_gst)}</small>
                </div>
              </div>
              {#if excluded}
                <p class="excluded">{excluded}</p>
              {/if}
              <div class="acts">
                <button class="btn btn--sm" type="button" onclick={() => openExpense(expense)}>Edit</button>
                {#if confirmDelete !== key}
                  <button class="btn btn--sm btn--ghost" type="button" onclick={() => (confirmDelete = key)}>
                    Delete
                  </button>
                {/if}
              </div>
              {#if confirmDelete === key}
                <div class="ask ask--danger">
                  <p>Remove this expense from {month}?</p>
                  <div class="ask__btns">
                    <button class="btn btn--sm btn--danger" type="button" onclick={() => void removeExpense(expense.id)}>Yes</button>
                    <button class="btn btn--sm" type="button" onclick={() => (confirmDelete = null)}>No</button>
                  </div>
                </div>
              {/if}
              {#if openForm === key}{@render expenseForm(expense.id)}{/if}
            </div>
          </div>
        </li>
      {/each}
    </ul>
  {/if}

  {#if openForm === 'expense:new'}
    <div class="addpanel">{@render expenseForm(null)}</div>
  {:else}
    <button class="a-cta add" type="button" onclick={() => openExpense(null)}>
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
      Add an expense
    </button>
  {/if}
{:else if tab === 'leases'}
  <p class="note">
    A standing cost is charged every month between its first and last. Untick <b>counts toward the
    monthly total</b> to leave it out of this month's overheads on the Performance Dashboard — for a
    cost that has ended, or one you want left out of the month for your own reasons.
  </p>

  {#if leases.length === 0}
    <div class="empty">
      <strong>No standing payments yet</strong>
      <span class="meta">Equipment leases, insurance, and anything else billed the same amount every month.</span>
    </div>
  {:else}
    <ul class="a-list">
      {#each leases as lease, i (lease.id)}
        {@const key = `lease:${lease.id}`}
        {@const running = runningIn(lease, month)}
        <li style="--i: {i}">
          <div class="a-card rate">
            <span
              class="a-card__rail"
              class:a-card__rail--muted={!running || lease.counts_toward_total === 0}
              aria-hidden="true"
            ></span>
            <div class="rate__body">
              <div class="rate__head">
                <div class="rate__name">
                  {lease.name}
                  <span class="rate__sub">{lease.kind_label} · <span class="a-num">{leaseMonths(lease)}</span></span>
                </div>
                <div class="rate__now">
                  <b class="a-num">{money(lease.monthly_amount)}</b>
                  <small class="a-num">
                    ex-GST {money(backOutGst(lease.monthly_amount, lease.had_gst === 1, gstOn(firstOfMonth(month))).amount_ex_gst)}
                  </small>
                </div>
              </div>
              {#if !running}
                <p class="excluded">Not running in {month}</p>
              {/if}
              <label class="check check--wide">
                <input
                  type="checkbox"
                  checked={lease.counts_toward_total === 1}
                  onchange={(e) => void setLeaseCounts(lease, (e.currentTarget as HTMLInputElement).checked)}
                />
                Counts toward the monthly total
              </label>
              <div class="acts">
                <button class="btn btn--sm" type="button" onclick={() => openLease(lease)}>Edit</button>
                {#if confirmDelete !== key}
                  <button class="btn btn--sm btn--ghost" type="button" onclick={() => (confirmDelete = key)}>
                    Delete
                  </button>
                {/if}
              </div>
              {#if confirmDelete === key}
                <div class="ask ask--danger">
                  <p>Remove {lease.name}? Every month it was charged on loses it.</p>
                  <div class="ask__btns">
                    <button class="btn btn--sm btn--danger" type="button" onclick={() => void removeLease(lease.id)}>Yes</button>
                    <button class="btn btn--sm" type="button" onclick={() => (confirmDelete = null)}>No</button>
                  </div>
                </div>
              {/if}
              {#if openForm === key}{@render leaseForm(lease.id)}{/if}
            </div>
          </div>
        </li>
      {/each}
    </ul>
  {/if}

  {#if openForm === 'lease:new'}
    <div class="addpanel">{@render leaseForm(null)}</div>
  {:else}
    <button class="a-cta add" type="button" onclick={() => openLease(null)}>
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
      Add a standing cost
    </button>
  {/if}
{:else if tab === 'categories'}
  <p class="note">
    A category is archived rather than deleted, so an expense filed under it keeps the only word
    saying what it was for.
  </p>
  <ul class="a-list">
    {#each liveCategories as category, i (category.id)}
      {@const key = `cat:${category.id}`}
      <li style="--i: {i}">
        <div class="a-card rate">
          <span class="a-card__rail" aria-hidden="true"></span>
          <div class="rate__body">
            <div class="rate__head">
              <div class="rate__name">
                {category.name}
                <span class="rate__sub">
                  {category.kind === 'transfer' ? 'Money moved, not spent' : 'Money spent'}
                </span>
              </div>
              <div class="rate__now">
                <small class="a-num">
                  {expenses.filter((e) => e.category_id === category.id).length} in {month}
                </small>
              </div>
            </div>
            <div class="acts">
              <button class="btn btn--sm" type="button" onclick={() => openCategory(category)}>Rename</button>
              {#if confirmDelete !== key}
                <button class="btn btn--sm btn--ghost" type="button" onclick={() => (confirmDelete = key)}>
                  Archive
                </button>
              {/if}
            </div>
            {#if confirmDelete === key}
              <div class="ask">
                <p>Hide {category.name} from the pickers? Expenses already filed under it keep it.</p>
                <div class="ask__btns">
                  <button class="btn btn--sm btn--danger" type="button" onclick={() => void setCategoryArchived(category, true)}>Yes, archive</button>
                  <button class="btn btn--sm" type="button" onclick={() => (confirmDelete = null)}>No</button>
                </div>
              </div>
            {/if}
            {#if openForm === key}
              {@render categoryForm(category.id)}
            {/if}
          </div>
        </div>
      </li>
    {/each}
  </ul>

  {#if openForm === 'cat:new'}
    <div class="addpanel">{@render categoryForm(null)}</div>
  {:else}
    <button class="a-cta add" type="button" onclick={() => openCategory(null)}>
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
      Add a category
    </button>
  {/if}

  {#if archivedCategories.length > 0}
    <ul class="a-list archived">
      {#each archivedCategories as category, i (category.id)}
        <li style="--i: {i}">
          <div class="a-card rate rate--removed">
            <span class="a-card__rail a-card__rail--muted" aria-hidden="true"></span>
            <div class="rate__body">
              <div class="rate__head">
                <div class="rate__name">{category.name}</div>
                <div class="rate__now"><span class="rate__none">archived</span></div>
              </div>
              <div class="acts">
                <button class="btn btn--sm" type="button" onclick={() => void setCategoryArchived(category, false)}>Restore</button>
              </div>
            </div>
          </div>
        </li>
      {/each}
    </ul>
  {/if}
{:else}
  <StatementImport {categories} {taxRates} onimported={() => void load(month)} />
{/if}

<style>
  .note {
    margin: 0 0 0.7rem;
    font-size: 0.84rem;
    color: var(--ink-soft);
  }

  .scopebar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem 0.8rem;
    padding: 0.6rem 0.75rem;
    margin-bottom: 0.9rem;
    background: var(--paper);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow);
  }
  .scopebar__label {
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--ink-faint);
  }

  /* The same pill the P&L's scope picker wears, so the two months read as the
     same control in two places. */
  .monthpick {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    flex: 1 1 9rem;
    min-width: 0;
    max-width: 14rem;
    min-height: var(--tap);
    padding: 0 0.75rem;
    border: 1.5px solid var(--line);
    border-radius: 999px;
    background: var(--paper);
  }
  .monthpick:focus-within {
    border-color: var(--brand);
    box-shadow: 0 0 0 3px var(--brand-tint);
  }
  .monthpick svg {
    width: 1.05rem;
    height: 1.05rem;
    flex: 0 0 auto;
    fill: none;
    stroke: var(--ink-faint);
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  .monthpick input {
    flex: 1 1 auto;
    width: 100%;
    min-width: 0;
    min-height: calc(var(--tap) - 4px);
    padding: 0;
    border: 0;
    background: transparent;
    font: inherit;
    font-size: 0.95rem;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: var(--ink);
    appearance: none;
  }
  .monthpick input:focus-visible {
    outline: none;
  }

  .tabs {
    display: flex;
    gap: 0.35rem;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 calc(-1 * var(--pad)) 0.9rem;
    padding: 0.15rem var(--pad) 0.45rem;
    scrollbar-width: thin;
  }
  .tab {
    flex: 0 0 auto;
    min-height: var(--tap);
    padding: 0 0.9rem;
    border: 1.5px solid var(--line);
    border-radius: 999px;
    background: var(--paper);
    font: inherit;
    font-size: 0.9rem;
    font-weight: 700;
    color: var(--ink-soft);
    cursor: pointer;
    touch-action: manipulation;
    white-space: nowrap;
  }
  .tab[aria-pressed='true'] {
    background: var(--brand);
    border-color: var(--brand-dark);
    color: #fff;
  }
  .tab:focus-visible {
    outline: 3px solid var(--brand);
    outline-offset: 2px;
  }

  /* ---- one card per thing ----------------------------------------------- */
  /* Not a link, so it must not lift on hover and promise a click it will not
     honour. */
  .rate {
    grid-template-columns: auto 1fr;
    align-items: stretch;
  }
  .rate:hover {
    transform: none;
    box-shadow: var(--shadow);
  }
  .rate:active {
    transform: none;
  }
  .rate--removed {
    background: var(--bg-sunken);
    box-shadow: none;
  }
  .rate__body {
    display: grid;
    gap: 0.5rem;
    min-width: 0;
    padding: 0.1rem 0;
  }
  .rate__head {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.2rem 0.8rem;
  }
  .rate__name {
    font-size: 1.08rem;
    font-weight: 700;
    letter-spacing: -0.01em;
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .rate__sub {
    display: block;
    font-size: 0.78rem;
    font-weight: 400;
    color: var(--ink-faint);
  }
  .rate__now {
    display: flex;
    align-items: baseline;
    gap: 0.4rem;
    flex-wrap: wrap;
  }
  .rate__now b {
    font-size: 1.08rem;
    font-weight: 700;
    letter-spacing: -0.01em;
  }
  .rate__now small {
    font-size: 0.76rem;
    color: var(--ink-faint);
  }
  .rate__none {
    font-size: 0.82rem;
    color: var(--ink-faint);
  }

  /* Where a line came from, so an imported expense is never mistaken for one
     that was typed. */
  .imported {
    display: inline-block;
    margin-left: 0.3rem;
    padding: 0 0.4rem;
    border-radius: 999px;
    background: var(--bg-sunken);
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--ink-soft);
  }

  /* Amber rather than the error red: a line kept out of the total is a choice
     the owner made, not a fault. */
  .excluded {
    margin: 0;
    font-size: 0.78rem;
    font-weight: 700;
    color: var(--warn);
  }

  .acts {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.4rem;
  }

  /* Inline banner: this app has no modals. */
  .ask {
    display: grid;
    gap: 0.5rem;
    padding: 0.6rem 0.7rem;
    border-radius: var(--radius);
    border-left: 4px solid var(--brand);
    background: var(--brand-tint);
  }
  .ask--danger {
    border-left-color: var(--hold);
    background: var(--hold-tint);
  }
  .ask p {
    margin: 0;
    font-size: 0.88rem;
    font-weight: 600;
  }
  .ask__btns {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
  }

  /* ---- the one inline form ---------------------------------------------- */
  .rateform {
    display: grid;
    gap: 0.5rem;
    padding: 0.7rem 0.75rem 0.8rem;
    border-radius: var(--radius);
    border-left: 4px solid var(--brand);
    background: var(--brand-tint);
    max-width: 32rem;
  }
  .rateform__btns {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  /* What the app will actually use, under the figure being typed, so the
     backed-out number is never a surprise. */
  .split {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 0.2rem 0.5rem;
    margin: 0;
    padding: 0.35rem 0.55rem;
    border-radius: var(--radius);
    background: var(--paper);
  }
  .split span {
    font-size: 0.66rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--ink-faint);
  }
  .split b {
    font-size: 1.02rem;
    letter-spacing: -0.01em;
  }
  .split small {
    font-size: 0.78rem;
    color: var(--ink-faint);
  }

  .check {
    display: inline-flex;
    align-items: center;
    gap: 0.55rem;
    min-height: var(--tap);
    padding: 0 0.85rem 0 0.65rem;
    border: 1.5px solid var(--line);
    border-radius: 999px;
    background: var(--paper);
    font-size: 0.9rem;
    font-weight: 700;
    color: var(--ink-soft);
    cursor: pointer;
    touch-action: manipulation;
  }
  .check input {
    width: 1.15rem;
    height: 1.15rem;
    margin: 0;
    flex: 0 0 auto;
    accent-color: var(--brand);
  }
  .check:has(input:checked) {
    border-color: var(--brand);
    color: var(--brand);
  }
  .check:focus-within {
    outline: 3px solid var(--brand);
    outline-offset: 2px;
  }
  /* This toggle decides whether a standing cost is in this month's total on
     the Performance Dashboard, so it gets the full width of the card rather
     than hiding at pill size. */
  .check--wide {
    justify-self: start;
    max-width: 100%;
  }

  .addpanel {
    background: var(--paper);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow);
    padding: 0.75rem;
    margin-top: 0.6rem;
  }
  .add {
    margin-top: 0.7rem;
  }
  .archived {
    margin-top: 0.9rem;
  }

  .empty {
    display: grid;
    gap: 0.4rem;
    justify-items: start;
    background: var(--paper);
    border-radius: var(--radius-lg);
    padding: 1.2rem;
    box-shadow: var(--shadow);
  }
</style>
